package com.mgacreative.hotel.feature.housekeeping.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import com.mgacreative.hotel.shared.domain.InventoryService
import com.mgacreative.hotel.domain.repository.RoomRepository
import com.mgacreative.hotel.domain.repository.InventoryRepository
import com.mgacreative.hotel.domain.repository.InventoryItem
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import io.github.jan.supabase.postgrest.postgrest

@Serializable
data class HousekeepingTask(
    val id: String, // We'll use Room ID here
    val room_id: String,
    val room_number: String,
    val status: String, // PENDING, IN_PROGRESS, DONE
    val assigned_to: String,
    val photo_url: String? = null
)

class HousekeepingViewModel(
    private val supabaseClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession,
    private val roomRepository: RoomRepository? = null,
    private val inventoryRepository: InventoryRepository? = null
) : ViewModel() {

    private val _tasks = MutableStateFlow<List<HousekeepingTask>>(emptyList())
    val tasks: StateFlow<List<HousekeepingTask>> = _tasks.asStateFlow()

    private val _errorMessage = MutableStateFlow<String?>(null)
    val errorMessage: StateFlow<String?> = _errorMessage.asStateFlow()

    private val _isUploading = MutableStateFlow(false)
    val isUploading: StateFlow<Boolean> = _isUploading.asStateFlow()

    fun loadTasks() {
        viewModelScope.launch {
            if (roomRepository == null) return@launch
            
            val rooms = roomRepository.getRooms()
            val mappedTasks = rooms.map { room ->
                val taskStatus = when (room.status) {
                    "DIRTY" -> "PENDING"
                    "IN_PROGRESS" -> "IN_PROGRESS"
                    "CLEAN", "AVAILABLE" -> "DONE"
                    else -> "DONE"
                }
                
                HousekeepingTask(
                    id = room.id ?: "",
                    room_id = room.id ?: "",
                    room_number = room.roomNumber,
                    status = taskStatus,
                    assigned_to = "Kat Görevlisi"
                )
            }
            _tasks.value = mappedTasks
        }
    }

    fun startTask(taskId: String) {
        viewModelScope.launch {
            if (roomRepository == null) return@launch
            try {
                roomRepository.updateRoomStatus(taskId, "IN_PROGRESS")
                loadTasks()
            } catch (e: Exception) {
                _errorMessage.value = "Hata oluştu: ${e.message}"
            }
        }
    }

    val inventoryItems: StateFlow<List<InventoryItem>> = (inventoryRepository?.observeInventoryItems()
        ?: MutableStateFlow(emptyList()))
        .stateIn(
            scope = viewModelScope,
            started = kotlinx.coroutines.flow.SharingStarted.WhileSubscribed(5000),
            initialValue = emptyList()
        )

    init {
        viewModelScope.launch {
            inventoryRepository?.loadInventoryItems()
        }
    }

    fun finishTask(taskId: String, photoBytes: ByteArray? = null) {
        finishTaskWithSupplies(taskId, emptyMap())
    }

    fun finishTaskWithSupplies(taskId: String, usedSupplies: Map<String, Int>) {
        viewModelScope.launch {
            _isUploading.value = true
            try {
                if (roomRepository != null) {
                    roomRepository.updateRoomStatus(taskId, "CLEAN")
                    
                    val task = _tasks.value.find { it.id == taskId }
                    if (task != null && inventoryRepository != null) {
                        try {
                            for ((itemId, amount) in usedSupplies) {
                                if (amount > 0) {
                                    inventoryRepository.addTransaction(
                                        itemId = itemId,
                                        type = "REDUCE",
                                        quantity = amount,
                                        description = "Oda ${task.room_number} Temizlik Kullanımı"
                                    )
                                }
                            }
                        } catch (e: Exception) {
                            e.printStackTrace()
                        }
                    }
                    loadTasks()
                }
                _errorMessage.value = null
            } catch (e: Exception) {
                _errorMessage.value = "Hata oluştu: ${e.message}"
            } finally {
                _isUploading.value = false
            }
        }
    }

    fun markAsDirty(taskId: String) {
        viewModelScope.launch {
            if (roomRepository == null) return@launch
            try {
                roomRepository.updateRoomStatus(taskId, "DIRTY")
                loadTasks() // Refresh list
            } catch (e: Exception) {
                _errorMessage.value = "Hata oluştu: ${e.message}"
            }
        }
    }

    fun reportMaintenance(roomId: String, issueDescription: String) {
        viewModelScope.launch {
            if (roomRepository != null) {
                roomRepository.updateRoomStatus(roomId, "MAINTENANCE")
                loadTasks() // Refresh list
            }
        }
    }

    fun clearError() {
        _errorMessage.value = null
    }

    fun addMinibarCharge(roomId: String, items: Map<String, Int>, totalAmount: Double) {
        viewModelScope.launch {
            try {
                val tenantId = tenantSession.currentTenantId ?: return@launch
                
                @kotlinx.serialization.Serializable
                data class ReservationResponse(val id: String)
                
                // Find active reservation for the room
                val reservations = supabaseClient.client.postgrest["reservations"]
                    .select {
                        filter {
                            eq("tenant_id", tenantId)
                            eq("room_id", roomId)
                            eq("status", "CHECKED_IN")
                        }
                    }
                    .decodeList<ReservationResponse>()
                
                val activeReservationId = reservations.firstOrNull()?.id
                if (activeReservationId == null) {
                    _errorMessage.value = "Hata: Bu odada aktif konaklayan müşteri bulunamadı."
                    return@launch
                }

                @kotlinx.serialization.Serializable
                data class FolioItemRequest(
                    val tenant_id: String,
                    val reservation_id: String,
                    val category: String,
                    val description: String,
                    val amount: Double
                )

                val desc = items.entries.joinToString(", ") { "${it.key} x${it.value}" }
                
                supabaseClient.client.postgrest["folio_items"].insert(
                    FolioItemRequest(
                        tenant_id = tenantId,
                        reservation_id = activeReservationId,
                        category = "MINIBAR",
                        description = "Minibar: $desc",
                        amount = totalAmount
                    )
                )
                
                // Also deduct from inventory if possible
                if (inventoryRepository != null) {
                    inventoryRepository.loadInventoryItems()
                    kotlinx.coroutines.delay(500)
                    val allItems = inventoryRepository.observeInventoryItems().first()
                    for ((itemName, amount) in items) {
                        val invItem = allItems.find { it.name.equals(itemName, ignoreCase = true) }
                        if (invItem != null) {
                            inventoryRepository.addTransaction(
                                itemId = invItem.id,
                                type = "REDUCE",
                                quantity = amount,
                                description = "Minibar Satışı (Oda: $roomId)"
                            )
                        }
                    }
                }
                
                _errorMessage.value = "Minibar harcaması başarıyla odaya yazıldı!"
            } catch (e: Exception) {
                e.printStackTrace()
                _errorMessage.value = "Hata oluştu: ${e.message}"
            }
        }
    }
}






